/**
 * Composant d'icône réutilisable
 * Permet d'afficher des icônes SVG depuis le dossier assets/icons
 *
 * Usage:
 * <apidae-icon name="bold/network_it_programming/sidebar_minimalistic_empty"></apidae-icon>
 * ou pour les icônes à la racine:
 * <apidae-icon name="sidebar_minimalistic_empty"></apidae-icon>
 */

import { html, unsafeCSS, CSSResultGroup, PropertyValues } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, property, state } from "lit/decorators.js";
import iconStyles from "../styles/icon.css?inline";
import { ApidaeVisualHost } from "./ApidaeVisualHost";

@customElement("apidae-icon")
export class Icon extends ApidaeVisualHost {
  static styles: CSSResultGroup = unsafeCSS(iconStyles);

  @property({ type: String })
  name: string = "";

  @property({ type: String })
  size: string = "24";

  @property({ type: String })
  color: string = "currentColor";

  @property({ type: String })
  class: string = "";

  /** Si true, ne pas remplacer les couleurs du SVG (pour icônes brandées, ex. réseaux sociaux) */
  @property({ type: Boolean, attribute: "preserve-colors" })
  preserveColors: boolean = false;

  /**
   * Pour les icônes à deux tons (ex. clock_circle : cercle + aiguilles).
   * Premier élément avec fill → currentColor, les suivants → innerFill.
   * Non défini = tout en currentColor (comportement par défaut).
   */
  @property({ type: String, attribute: "inner-fill" })
  innerFill: string = "";

  @state()
  private iconSvg: string | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadIcon();
  }

  async updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has("name")) {
      await this.loadIcon();
    }
  }

  private async loadIcon(): Promise<void> {
    if (!this.name) {
      this.iconSvg = null;
      return;
    }

    try {
      // Chemin sans transformation : format bold/category/file_name (minuscules, underscores, pas d'espaces ni virgules)
      const iconPath = `${this.name}.svg`;
      let baseUrl = "";

      if (import.meta.env.DEV) {
        baseUrl = `/src/assets/icons/${iconPath}`;
      } else {
        const scripts = Array.from(document.getElementsByTagName("script"));
        const widgetScript = scripts.find(
          (s) => s.src && s.src.includes("apidae-widgets"),
        );

        if (widgetScript && widgetScript.src) {
          const scriptUrl = new URL(widgetScript.src);
          const scriptPath = scriptUrl.pathname;
          const scriptDir = scriptPath.substring(
            0,
            scriptPath.lastIndexOf("/"),
          );
          baseUrl = `${scriptUrl.origin}${scriptDir}/assets/icons/${iconPath}`;
        } else {
          baseUrl = `./assets/icons/${iconPath}`;
        }
      }

      const response = await fetch(baseUrl);
      if (!response.ok) {
        console.warn(
          `Icon ${this.name} not found at ${baseUrl} (status: ${response.status})`,
        );
        this.iconSvg = null;
        return;
      }

      const svgText = await response.text();
      this.iconSvg = svgText;
      this.requestUpdate();
    } catch (error) {
      console.warn(`Error loading icon ${this.name}:`, error);
      this.iconSvg = null;
    }
  }

  render() {
    if (!this.iconSvg) {
      return html``;
    }

    // Parser le SVG et remplacer les attributs de couleur
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(this.iconSvg, "image/svg+xml");
    const svgElement = svgDoc.querySelector("svg");

    if (!svgElement) {
      return html``;
    }

    // Mettre à jour la taille - utiliser la même valeur pour width et height pour un rendu carré
    svgElement.setAttribute("width", this.size);
    svgElement.setAttribute("height", this.size);
    svgElement.style.width = `${this.size}px`;
    svgElement.style.height = `${this.size}px`;

    // Remplacer les couleurs par currentColor sauf si preserveColors (icônes brandées)
    const isSidebarEmpty = this.name.includes("sidebar_minimalistic_empty");
    const elementsWithFill = ["path", "circle", "ellipse", "rect"];

    if (!this.preserveColors) {
      // Toujours appliquer stroke=currentColor à tout élément qui a un stroke (y compris icônes stroke-only type sidebar)
      elementsWithFill.forEach((tag) => {
        svgElement.querySelectorAll(tag).forEach((el) => {
          if (el.getAttribute("stroke")) {
            el.setAttribute("stroke", "currentColor");
          }
        });
      });

      const filledElements = elementsWithFill.flatMap((tag) =>
        Array.from(svgElement.querySelectorAll(tag)).filter(
          (el) => el.getAttribute("fill") && el.getAttribute("fill") !== "none",
        ),
      );
      const useTwoTone = this.innerFill && filledElements.length > 1;

      // Fill : currentColor (ou innerFill pour deux tons) pour les éléments qui ont un fill explicite ≠ none (sidebar n'est pas dans filledElements)
      filledElements.forEach((el, index) => {
        el.setAttribute(
          "fill",
          useTwoTone && index > 0 ? this.innerFill : "currentColor",
        );
      });

      // path avec fill : stroke-width à 0 pour cohérence (icônes filled)
      svgElement.querySelectorAll("path").forEach((path) => {
        if (!isSidebarEmpty && path.getAttribute("fill")) {
          path.setAttribute("stroke-width", "0");
        }
      });

      // Icône stroke-only (sidebar_minimalistic_empty) : forcer fill none partout et affiner le trait en code
      if (isSidebarEmpty) {
        svgElement.querySelectorAll("path").forEach((path) => {
          path.setAttribute("fill", "none");
          const w = path.getAttribute("stroke-width");
          if (w && parseFloat(w) > 1) {
            path.setAttribute("stroke-width", "1.5");
          }
        });
      }
    }

    return html`
      <div
        class="icon-container ${this.class} ${this.preserveColors
          ? "icon-container--preserve-colors"
          : ""} ${isSidebarEmpty ? "icon-container--stroke-only" : ""}"
        style="color: ${this.color}; width: ${this.size}px; height: ${this
          .size}px;"
      >
        ${unsafeHTML(svgElement.outerHTML)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-icon": Icon;
  }
}
